<template>
    <div class="ds-settings-page">
        <SettingsNav />

        <main class="ds-settings-main" :aria-busy="editor.saving.value || undefined">
            <div v-if="editor.section.value === 'general'" class="ds-settings-stack">
                <GlobalSettings />
                <EmployeeStatisticsSettings />
                <LunchSettings />
            </div>
            <RolesSettings v-else-if="editor.section.value === 'roles'" />
            <PositionSettings v-else-if="editor.section.value === 'positions'" />
            <ExchangeSettings v-else-if="editor.section.value === 'exchange'" />
        </main>

        <SettingsChangesPanel />

        <!-- Dirty guard в стиле RD-148: смена раздела/роли и внутренний route. -->
        <Modal v-if="editor.guard.open" @close="editor.guard.busy || editor.guardStay()">
            <div class="ds-settings-guard" role="alertdialog" aria-labelledby="settings-guard-title" aria-describedby="settings-guard-text">
                <div id="settings-guard-title" class="ds-glass-dialog-title">Сохранить изменения?</div>
                <div id="settings-guard-text" class="ds-glass-dialog-text">
                    {{ guardText }}
                </div>
                <div class="ds-glass-dialog-actions">
                    <button class="ds-glass-btn ds-glass-btn-ghost" type="button" :disabled="editor.guard.busy" @click="editor.guardStay()">Остаться</button>
                    <button class="ds-glass-btn ds-glass-btn-secondary" type="button" :disabled="editor.guard.busy" @click="editor.guardDiscard()">Не сохранять</button>
                    <button
                        class="ds-glass-btn ds-glass-btn-primary"
                        type="button"
                        :disabled="editor.guard.busy || editor.currentInvalid.value"
                        @click="editor.guardSave()"
                    >{{ editor.guard.busy ? 'Сохранение…' : 'Сохранить' }}</button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import Modal from '@/ui/Modal.vue'
import SettingsNav from '@/components/settings/SettingsNav.vue'
import SettingsChangesPanel from '@/components/settings/SettingsChangesPanel.vue'
import GlobalSettings from '@/components/settings/GlobalSettings.vue'
import EmployeeStatisticsSettings from '@/components/settings/EmployeeStatisticsSettings.vue'
import LunchSettings from '@/components/settings/LunchSettings.vue'
import ExchangeSettings from '@/components/settings/ExchangeSettings.vue'
import PositionSettings from '@/components/settings/PositionSettings.vue'
import RolesSettings from '@/components/roles/RolesSettings.vue'
import { createSettingsEditor, settingsEditorKey } from '@/components/settings/useSettingsEditor'

const editor = createSettingsEditor()
provide(settingsEditorKey, editor)

const pluralChanges = (count: number) => {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return 'несохранённая правка'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'несохранённые правки'
    return 'несохранённых правок'
}

const guardText = computed(() => {
    const count = editor.currentChanges.value.length
    const scope = editor.section.value === 'roles' && editor.selectedRole.value
        ? `В роли «${editor.selectedRole.value.name}»`
        : 'В этом разделе'
    const invalid = editor.currentInvalid.value ? ' Есть некорректные значения — исправьте их или не сохраняйте.' : ''
    // Неразрывный пробел: число не отрывается от слова при переносе.
    return `${scope} ${count} ${pluralChanges(count)}.${invalid}`
})

onBeforeRouteLeave(async () => {
    if (editor.saving.value) return false
    if (!editor.hasAnyChanges.value) return true
    // Правки бывают только в текущей области: уход из неё внутри раздела проходит тот же guard.
    const proceed = await editor.confirmLeaveCurrentArea()
    if (proceed && editor.hasAnyChanges.value) editor.resetAll()
    return proceed
})

// Закрытие/обновление вкладки — стандартное предупреждение браузера.
function onBeforeUnload(event: BeforeUnloadEvent) {
    if (!editor.hasAnyChanges.value && !editor.saving.value) return
    event.preventDefault()
    event.returnValue = ''
}

onMounted(() => {
    window.addEventListener('beforeunload', onBeforeUnload)
    editor.loadAll()
})

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', onBeforeUnload)
})
</script>
